"use client";

import { c as _c } from "react/compiler-runtime";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import { WaveLeaderboardIdentity } from "@/components/waves/leaderboard/identity/WaveLeaderboardIdentity";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import React, { useCallback } from "react";
import type { WaveLeaderboardGridMode } from "./WaveLeaderboardGrid";
import { WaveLeaderboardGridItemCompactFooter } from "./WaveLeaderboardGridItemCompactFooter";
import { WaveLeaderboardGridItemMobileActionsMenu } from "./WaveLeaderboardGridItemMobileActionsMenu";
import { WaveLeaderboardGridItemViewport } from "./WaveLeaderboardGridItemViewport";
import { WaveLeaderboardGridItemVotingModal } from "./WaveLeaderboardGridItemVotingModal";
interface WaveLeaderboardGridItemProps {
  readonly drop: ExtendedDrop;
  readonly mode: WaveLeaderboardGridMode;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const canOpenGridItemFromClick = ({
  isMenuOpen,
  target
}: {
  readonly isMenuOpen: boolean;
  readonly target: HTMLElement;
}): boolean => {
  if (isMenuOpen) {
    return false;
  }
  return !target.closest("a, button");
};
const isGridItemOpenKey = (key: string): boolean => key === "Enter" || key === " ";
export const WaveLeaderboardGridItem: React.FC<WaveLeaderboardGridItemProps> = t0 => {
  const $ = _c(69);
  const {
    drop,
    mode,
    isVotingClosed: t1,
    isVotingControlsLocked: t2,
    winningThreshold,
    onDropClick
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const isVotingControlsLocked = t2 === undefined ? false : t2;
  const isCompactMode = mode === "compact";
  const isContentOnlyMode = mode === "content_only";
  const isCuratable = drop.context_profile_context?.curatable ?? false;
  const isCurated = drop.context_profile_context?.curated ?? false;
  const canOpenDrop = drop.drop_type !== ApiDropType.Chat;
  const isMobileScreen = useIsMobileScreen();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let t3;
  if ($[0] !== hasTouchScreen) {
    t3 = {
      hasTouchScreen,
      preventDefault: false
    };
    $[0] = hasTouchScreen;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t3);
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const {
    isOpen: isVoteModalOpen,
    open: openVoteModal,
    close: closeVoteModal
  } = useVotingModalState(isVotingActionLocked);
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const canShowVotingAction = canShowVote && !isVotingActionLocked;
  let t4;
  if ($[2] !== drop.id) {
    t4 = drop.id.startsWith("temp-");
    $[2] = drop.id;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const canCopyLink = !t4;
  const hasDesktopContentOnlyActions = canOpenDrop || isCuratable || canShowVotingAction;
  const hasMobileContentOnlyActions = hasDesktopContentOnlyActions || canCopyLink;
  const showDesktopContentOnlyActions = isContentOnlyMode && !hasTouchScreen && hasDesktopContentOnlyActions;
  const showMobileContentOnlyActions = isContentOnlyMode && hasTouchScreen && hasMobileContentOnlyActions;
  let t5;
  if ($[4] !== openVoteModal) {
    t5 = () => {
      openVoteModal();
    };
    $[4] = openVoteModal;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleVoteButtonClick = t5;
  let t6;
  if ($[6] !== drop || $[7] !== isMobileScreen || $[8] !== onDropClick) {
    t6 = () => {
      startDropOpen({
        dropId: drop.id,
        waveId: drop.wave.id,
        source: "leaderboard_grid",
        isMobile: isMobileScreen
      });
      onDropClick(drop);
    };
    $[6] = drop;
    $[7] = isMobileScreen;
    $[8] = onDropClick;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const openDrop = t6;
  let t7;
  if ($[10] !== isActive || $[11] !== openDrop || $[12] !== showMobileContentOnlyActions) {
    t7 = event => {
      const target = event.target as HTMLElement;
      if (!canOpenGridItemFromClick({
        isMenuOpen: showMobileContentOnlyActions && isActive,
        target
      })) {
        return;
      }
      openDrop();
    };
    $[10] = isActive;
    $[11] = openDrop;
    $[12] = showMobileContentOnlyActions;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onCardClick = t7;
  let t8;
  if ($[14] !== isActive || $[15] !== openDrop || $[16] !== showMobileContentOnlyActions) {
    t8 = event_0 => {
      if (!isGridItemOpenKey(event_0.key)) {
        return;
      }
      const target_0 = event_0.target as HTMLElement;
      if (!canOpenGridItemFromClick({
        isMenuOpen: showMobileContentOnlyActions && isActive,
        target: target_0
      })) {
        return;
      }
      event_0.preventDefault();
      openDrop();
    };
    $[14] = isActive;
    $[15] = openDrop;
    $[16] = showMobileContentOnlyActions;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const onCardKeyDown = t8;
  const t9 = `wave-leaderboard-grid-item-${drop.id}`;
  let t10;
  if ($[18] !== showMobileContentOnlyActions || $[19] !== touchHandlers) {
    t10 = showMobileContentOnlyActions ? touchHandlers : {};
    $[18] = showMobileContentOnlyActions;
    $[19] = touchHandlers;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== canOpenDrop || $[22] !== canShowVotingAction || $[23] !== drop || $[24] !== handleVoteButtonClick || $[25] !== isCompactMode || $[26] !== isContentOnlyMode || $[27] !== isCuratable || $[28] !== isCurated || $[29] !== openDrop || $[30] !== showDesktopContentOnlyActions) {
    t11 = <WaveLeaderboardGridItemViewport drop={drop} isCompactMode={isCompactMode} isContentOnlyMode={isContentOnlyMode} showDesktopContentOnlyActions={showDesktopContentOnlyActions} canOpenDrop={canOpenDrop} isCuratable={isCuratable} isCurated={isCurated} canShowVotingAction={canShowVotingAction} onOpenDrop={openDrop} onVoteButtonClick={handleVoteButtonClick} />;
    $[21] = canOpenDrop;
    $[22] = canShowVotingAction;
    $[23] = drop;
    $[24] = handleVoteButtonClick;
    $[25] = isCompactMode;
    $[26] = isContentOnlyMode;
    $[27] = isCuratable;
    $[28] = isCurated;
    $[29] = openDrop;
    $[30] = showDesktopContentOnlyActions;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== drop || $[33] !== isContentOnlyMode) {
    t12 = isContentOnlyMode && <WaveLeaderboardIdentity drop={drop} variant="responsive" cardVariant="chat" className="tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800/50 tw-bg-iron-950/50 tw-p-3" supplementFullWidth={true} />;
    $[32] = drop;
    $[33] = isContentOnlyMode;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== canShowVotingAction || $[36] !== drop || $[37] !== handleVoteButtonClick || $[38] !== isCompactMode || $[39] !== isVotingClosed || $[40] !== winningThreshold) {
    t13 = isCompactMode && <WaveLeaderboardGridItemCompactFooter drop={drop} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} canShowVotingAction={canShowVotingAction} onVoteButtonClick={handleVoteButtonClick} />;
    $[35] = canShowVotingAction;
    $[36] = drop;
    $[37] = handleVoteButtonClick;
    $[38] = isCompactMode;
    $[39] = isVotingClosed;
    $[40] = winningThreshold;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== closeVoteModal || $[43] !== drop || $[44] !== isCompactMode || $[45] !== isContentOnlyMode || $[46] !== isMobileScreen || $[47] !== isVoteModalOpen) {
    t14 = (isCompactMode || isContentOnlyMode) && <WaveLeaderboardGridItemVotingModal drop={drop} isOpen={isVoteModalOpen} isMobileScreen={isMobileScreen} onClose={closeVoteModal} />;
    $[42] = closeVoteModal;
    $[43] = drop;
    $[44] = isCompactMode;
    $[45] = isContentOnlyMode;
    $[46] = isMobileScreen;
    $[47] = isVoteModalOpen;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== canOpenDrop || $[50] !== canShowVotingAction || $[51] !== drop || $[52] !== handleVoteButtonClick || $[53] !== isActive || $[54] !== isCuratable || $[55] !== isCurated || $[56] !== setIsActive || $[57] !== showMobileContentOnlyActions) {
    t15 = showMobileContentOnlyActions && <WaveLeaderboardGridItemMobileActionsMenu drop={drop} isOpen={isActive} setIsActive={setIsActive} canOpenDrop={canOpenDrop} isCuratable={isCuratable} isCurated={isCurated} canShowVotingAction={canShowVotingAction} onVoteClick={handleVoteButtonClick} />;
    $[49] = canOpenDrop;
    $[50] = canShowVotingAction;
    $[51] = drop;
    $[52] = handleVoteButtonClick;
    $[53] = isActive;
    $[54] = isCuratable;
    $[55] = isCurated;
    $[56] = setIsActive;
    $[57] = showMobileContentOnlyActions;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== onCardClick || $[60] !== onCardKeyDown || $[61] !== t10 || $[62] !== t11 || $[63] !== t12 || $[64] !== t13 || $[65] !== t14 || $[66] !== t15 || $[67] !== t9) {
    t16 = <div role="button" tabIndex={0} data-testid={t9} onClick={onCardClick} onKeyDown={onCardKeyDown} className="tw-group tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-p-0 tw-transition desktop-hover:hover:tw-border-iron-700" {...t10}>{t11}{t12}{t13}{t14}{t15}</div>;
    $[59] = onCardClick;
    $[60] = onCardKeyDown;
    $[61] = t10;
    $[62] = t11;
    $[63] = t12;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
    $[67] = t9;
    $[68] = t16;
  } else {
    t16 = $[68];
  }
  return t16;
};